import type { RequestOptions } from 'graphql-request';

import type { AsnDTO, AsnLineDTO, AsnStatus, CreateAsnDTO } from '@invenco/supply-interface';

import { DEFAULT_PAGE_SIZE } from '@/constants';
import {
  type AsnsQueryQuery,
  type AsnsQueryQueryVariables,
  type AsnsBoolExp,
  type AsnsOrderByExp,
  OrderBy,
} from '@/graphql/generated/seller/graphql';

import { BaseGateway, QueryRequestOptions } from '../BaseGateway';
import type {
  GatewayArrayFilter,
  GatewayName,
  GatewayQueryProps,
  PaginatedResponse,
} from '../types';
import { asnsQuery } from './gql/seller/supply';
import { graphqlOperatorMap } from './graphql';

export type SortField =
  | 'id'
  | 'name'
  | 'reference'
  | 'expectedAt'
  | 'locationName'
  | 'status'
  | 'qtyExpected'
  | 'qtyReceived'
  | 'closedAt'
  | 'createdAt';

export type QueryAsnsProps = GatewayQueryProps<{
  sortKey: SortField;
  filters: {
    status: GatewayArrayFilter;
    locationName: GatewayArrayFilter;
  };
}>;

export class SupplyGateway extends BaseGateway {
  name: GatewayName = 'supply';

  async getAsn(id: string, options?: QueryRequestOptions): Promise<AsnDTO> {
    const { data } = await this.httpGet(`/v1/asns/${id}`, options);
    return data.asn;
  }

  async getAsns(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
      statuses?: AsnStatus[];
      locationIds?: string[];
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<AsnDTO>> {
    const { data } = await this.httpGet('/v1/asns', {
      params: {
        take: query?.take,
        cursor: query?.cursor,
        q: query?.search,
        statuses: query?.statuses?.join(','),
        locations: query?.locationIds?.join(','),
      },
      ...options,
    });
    return { ...data, items: data.asns };
  }

  async createAsn(asn: CreateAsnDTO): Promise<AsnDTO> {
    const { data } = await this.httpPost<{ asn: AsnDTO }>('/v1/asns', { data: { asn } });
    return data.asn;
  }

  async updateAsn(asnId: string, asn: Partial<AsnDTO>): Promise<AsnDTO> {
    const { data } = await this.httpPatch<{ asn: AsnDTO }>(`/v1/asns/${asnId}`, { data: { asn } });
    return data.asn;
  }

  async bookAsn(asnId: string): Promise<AsnDTO> {
    const { data } = await this.httpPost<{ asn: AsnDTO }>(`/v1/asns/${asnId}/book`);
    return data.asn;
  }

  async cancelAsn(asnId: string): Promise<AsnDTO> {
    const { data } = await this.httpPost<{ asn: AsnDTO }>(`/v1/asns/${asnId}/cancel`);
    return data.asn;
  }

  async addAsnLine(asnId: string, asnLine: Partial<AsnLineDTO>): Promise<AsnLineDTO> {
    const { data } = await this.httpPost<{ asnLine: AsnLineDTO }>(`/v1/asns/${asnId}/asn_lines`, {
      data: { asnLine },
    });
    return data.asnLine;
  }

  async updateAsnLine(
    asnId: string,
    asnLineId: string,
    asnLine: Partial<AsnLineDTO>,
  ): Promise<AsnLineDTO> {
    const { data } = await this.httpPatch<{ asnLine: AsnLineDTO }>(
      `/v1/asns/${asnId}/asn_lines/${asnLineId}`,
      { data: { asnLine } },
    );
    return data.asnLine;
  }

  async deleteAsnLine(asnId: string, asnLineId: string): Promise<void> {
    await this.httpDelete(`/v1/asns/${asnId}/asn_lines/${asnLineId}`);
  }

  async recostAsnLines(asnId: string, asnLines: Partial<AsnLineDTO>[]): Promise<AsnDTO> {
    const { data } = await this.httpPost<{ asn: AsnDTO }>(`/v1/asns/${asnId}/recost`, {
      data: { recostLines: asnLines },
    });
    return data.asn;
  }

  async queryAsns(
    { filters, take, search, offset = 0, sortBy }: QueryAsnsProps,
    options: { signal?: RequestOptions['signal'] },
  ): Promise<AsnsQueryQuery> {
    // Ensure limit is always a positive number
    const limit = Math.max(1, Math.abs(take ?? DEFAULT_PAGE_SIZE));
    const whereClause: AsnsBoolExp[] = [];

    if (search) {
      const searchPattern = `%${search}%`;
      whereClause.push({
        _or: [{ name: { _ilike: searchPattern } }, { reference: { _ilike: searchPattern } }],
      });
    }

    if (filters?.status && filters.status.value.length > 0) {
      whereClause.push({
        status: { [graphqlOperatorMap[filters.status.operator]]: filters.status.value },
      });
    }
    if (filters?.locationName && filters.locationName.value.length > 0) {
      whereClause.push({
        locationName: {
          [graphqlOperatorMap[filters.locationName.operator]]: filters.locationName.value,
        },
      });
    }

    const orderBy =
      sortBy?.map<AsnsOrderByExp>((s) => ({
        [s.key]: s.direction === 'desc' ? OrderBy.Desc : OrderBy.Asc,
      })) ?? [];

    const variables: AsnsQueryQueryVariables = {
      limit,
      offset,
      where: whereClause.length > 0 ? { _and: whereClause } : {},
      orderBy,
    };

    return this.graphqlQuery(asnsQuery, variables, options.signal);
  }
}
